import React from 'react';
import { ProfileField } from './ProfileField';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import type { SubscriptionTier } from '../../config/subscriptionTiers';
import type { Profile, AddressEntry } from '../../types/profile';
import type { FieldType } from '../../pages/Settings';

interface ProfileFieldGroupProps {
  title: string;
  tier: SubscriptionTier;
  fields: {
    key: keyof Profile | string;
    label: string;
    type?: string;
    placeholder?: string;
    defaultLink?: string;
    viewText?: string;
    helpText?: string;
  }[];
  profileData: Profile | null;
  onEdit: (field: FieldType) => void;
}

export const ProfileFieldGroup: React.FC<ProfileFieldGroupProps> = ({
  title,
  tier,
  fields,
  profileData,
  onEdit
}) => {
  const { tier: currentTier, showUpgradeModal } = useSubscription();
  
  // Helper to determine if current subscription can access this tier's features
  const canAccess = () => {
    if (tier === 'FREE') return true;
    if (tier === 'PRO') return currentTier === 'PRO' || currentTier === 'BUSINESS_PRO';
    if (tier === 'BUSINESS_PRO') return currentTier === 'BUSINESS_PRO';
    return false;
  };

  const handleFieldClick = (field: string) => {
    if (!canAccess()) {
      showUpgradeModal(field, tier);
      return;
    }
    onEdit(field as FieldType);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-1">
        <h3 className="text-lg font-medium text-gray-700">{title}</h3>
        {!canAccess() && (
          <div className="flex items-center space-x-1 text-gray-500">
            <LockClosedIcon className="h-4 w-4" />
            <span className="text-sm">{tier} Required</span>
          </div>
        )}
        {tier === 'BUSINESS_PRO' && title !== '' && (
          <p className="text-sm text-gray-500 italic">
            Business Pro includes all Pro features
          </p>
        )}
      </div>

      <div className="space-y-4">
        {fields.map(({ key, label, type, placeholder, defaultLink, viewText, helpText }) => {
          let fieldValue = '';
          if (typeof key === 'string' && key.startsWith('address.')) {
            const addressField = key.split('.')[1] as keyof AddressEntry;
            const addressValue = profileData?.address?.[0]?.[addressField];
            fieldValue = String(addressValue ?? '');
          } else {
            const value = profileData?.[key as keyof Profile];
            fieldValue = typeof value === 'string' ? value : String(value ?? '');
          }

          return (
            <ProfileField
              key={key}
              label={label}
              value={fieldValue}
              onEdit={() => handleFieldClick(key)}
              locked={!canAccess()}
              type={type}
              placeholder={placeholder}
              defaultLink={defaultLink}
              viewText={viewText}
              helpText={helpText}
            />
          );
        })}
      </div>
    </div>
  );
};
